'use client';

import {
  BodyDefault,
  Button,
  HeadingItem,
  IconButton,
  MetaDefault,
  Spinner,
  Tooltip,
  toasterApi,
} from '@terra/ui-design-system';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  Cancel01Icon,
  PlayCircleIcon,
  SecurityCheckIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useTriggerDeepSecurityScan } from '../../hooks/api/useTriggerDeepSecurityScan';

export function DeepSecurityScanPanel({
  applicationId,
  onClose,
}: {
  applicationId: string;
  onClose?: () => void;
}) {
  const t = useTranslations('deepSecurityScan');
  const tCommon = useTranslations('common');
  const triggerMutation = useTriggerDeepSecurityScan({ applicationId });

  const handleTrigger = async () => {
    try {
      await triggerMutation.mutateAsync();
      toasterApi.success({ title: t('startSuccessTitle') });
    } catch (error) {
      toasterApi.error({
        title: t('startErrorTitle'),
        description:
          error instanceof Error ? error.message : t('startErrorDescription'),
      });
    }
  };

  return (
    <VStack gap="0" w="full" alignItems="stretch">
      <HStack
        justifyContent="space-between"
        w="full"
        px="3"
        py="3"
        gap="2"
        borderBottomWidth="1px"
        borderColor="border.muted"
      >
        <HStack gap="2" alignItems="center">
          <SecurityCheckIcon size={18} />
          <HeadingItem>{t('title')}</HeadingItem>
        </HStack>

        <HStack gap="2">
          <Button
            size="xs"
            variant="outline"
            onClick={handleTrigger}
            disabled={triggerMutation.isPending}
          >
            {triggerMutation.isPending ? (
              <HStack gap="2">
                <Spinner size="xs" />
                <span>{t('triggering')}</span>
              </HStack>
            ) : (
              <HStack gap="2">
                <PlayCircleIcon size={14} />
                <span>{t('run')}</span>
              </HStack>
            )}
          </Button>
          {onClose ? (
            <Tooltip
              title={tCommon('close')}
              positioning={{ strategy: 'fixed' }}
            >
              <IconButton
                variant="ghost"
                size="xs"
                onClick={onClose}
                aria-label={tCommon('close')}
              >
                <Cancel01Icon size={16} />
              </IconButton>
            </Tooltip>
          ) : null}
        </HStack>
      </HStack>

      <VStack gap="2" w="full" px="3" py="3" alignItems="stretch">
        <BodyDefault color="fg.muted">{t('description')}</BodyDefault>
        <MetaDefault color="fg.muted">{t('helper')}</MetaDefault>
      </VStack>
    </VStack>
  );
}
